import classNames from 'classnames';
import { format as formatDate } from 'date-fns';
import React, { ReactNode, useState } from 'react';

import { ICreateProfilePayload } from '../../types/Profile';
import { getErrorMessage } from '../../utils/errors';
import Form from '../Form';
import { FormGroup } from '../FormGroup/FormGroup';
import { CityOption, CitySelect } from '../FormInput/CitySelect';
import { DateInput } from '../FormInput/DateInput';
import { FormInput } from '../FormInput/FormInput';
import { TimeInput } from '../FormInput/TimeInput';
import { Button } from '../Button/Button';

import defaultStyles from './CreateProfileForm.module.scss';

interface FormValues {
  firstName: string;
  lastName: string;
  middleName?: string;
  birthDate: Date | null;
  birthTime: Date | null;
  city: string;
  cityOption?: CityOption | null;
  timezone?: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  birthTime?: string;
  city?: string;
  global?: string;
}

interface CreateProfileFormProps {
  className?: string;
  header?: ReactNode;
  initialValues?: FormValues;
  onSubmit: (payload: ICreateProfilePayload) => Promise<void> | void;
  buttonLabel?: string;
  showMiddleName?: boolean;
  styles?: { [key: string]: string };
  dateTimeGroupClassName?: string;
}

export function CreateProfileForm({
  className,
  header,
  initialValues = {
    firstName: '',
    lastName: '',
    middleName: '',
    birthDate: null,
    birthTime: null,
    city: '',
  },
  onSubmit,
  buttonLabel = 'Создать профиль',
  showMiddleName = true,
  styles = {},
  dateTimeGroupClassName,
}: CreateProfileFormProps) {
  const [formValues, setFormValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const mergedStyles = { ...defaultStyles, ...styles };

  const handleDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormValues(prev => ({ ...prev, [id]: value }));
    setErrors(prev => ({ ...prev, [id]: undefined, global: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formValues.firstName.trim()) newErrors.firstName = 'Имя обязательно';
    if (!formValues.lastName.trim()) newErrors.lastName = 'Фамилия обязательна';
    if (!formValues.birthDate) newErrors.birthDate = 'Дата обязательна';
    if (!formValues.birthTime) newErrors.birthTime = 'Время обязательно';

    if (!formValues.cityOption) {
      newErrors.city = 'Город обязателен';
    } else if (!formValues.timezone) {
      newErrors.city = 'Не удалось определить таймзону';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);

      const cityOpt = formValues.cityOption!;
      const datePart = formatDate(formValues.birthDate!, 'yyyy-MM-dd');
      const timePart = formatDate(formValues.birthTime!, 'HH:mm');
      const bornAt = new Date(`${datePart}T${timePart}:00Z`).toISOString();

      const payload: ICreateProfilePayload = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        middleName: formValues.middleName || '',
        bornAt,
        location: {
          name: cityOpt.label,
          timezone: formValues.timezone!,
        },
      };

      await onSubmit(payload);
    } catch (err: unknown) {
      setErrors(prev => ({
        ...prev,
        global: getErrorMessage(err),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className={classNames(mergedStyles.form, className)}>
      {header && <div className={mergedStyles.formHeader}>{header}</div>}

      {errors.global && <div className={mergedStyles.globalError}>{errors.global}</div>}

      <div className={mergedStyles.formBody}>
        <FormGroup>
          <FormInput
            id="firstName"
            type="text"
            label="Имя"
            value={formValues.firstName}
            onChange={handleDataChange}
            placeholder="Введите свое имя"
            error={errors.firstName}
            required
          />
          <FormInput
            id="lastName"
            type="text"
            label="Фамилия"
            value={formValues.lastName}
            onChange={handleDataChange}
            placeholder="Введите свою фамилию"
            error={errors.lastName}
            required
          />
        </FormGroup>

        {showMiddleName && (
          <FormGroup>
            <FormInput
              id="middleName"
              type="text"
              label="Отчество"
              value={formValues.middleName}
              onChange={handleDataChange}
              placeholder="Введите свое отчество"
            />
          </FormGroup>
        )}

        <FormGroup className={dateTimeGroupClassName}>
          <DateInput
            id="birthDate"
            label="Дата рождения"
            value={formValues.birthDate}
            onChange={date => {
              setFormValues(prev => ({ ...prev, birthDate: date }));
              setErrors(prev => ({ ...prev, birthDate: undefined, global: undefined }));
            }}
            error={errors.birthDate}
            required
          />
          <TimeInput
            id="birthTime"
            label="Время рождения"
            value={formValues.birthTime}
            onChange={time => {
              setFormValues(prev => ({ ...prev, birthTime: time }));
              setErrors(prev => ({ ...prev, birthTime: undefined, global: undefined }));
            }}
            error={errors.birthTime}
            required
          />
        </FormGroup>

        <FormGroup>
          <CitySelect
            id="city"
            label="Город рождения"
            value={formValues.cityOption || null}
            onChange={(opt, tz) =>
              setFormValues(prev => ({
                ...prev,
                city: opt?.value ?? '',
                cityOption: opt || null,
                timezone: tz || '',
              }))
            }
            error={errors.city}
            required
          />
        </FormGroup>
      </div>

      <div className={mergedStyles.formAction}>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Создание...' : buttonLabel}
        </Button>
      </div>
    </Form>
  );
}
